import type { PublicPinnedTrophy } from '@crowdfpl/domain/gamification';
import { useQuery } from '@tanstack/react-query';
import { getProfiles, getProfilesLeaderboard } from '@/api/profiles';
import type { LeaderboardTimeframe } from '@/helpers/leaderboard-period-label';
import type { LeaderboardScope, LeaderboardScopeCards } from '@/types/profiles';

export interface LeaderboardEntry {
  rank: number;
  username: string;
  avatar_url?: string;
  predictor_rating: number;
  success_rate: number;
  total_predictions: number;
  user_id: string;
  // GW-specific fields (only when gameweek_id is provided)
  rating_change?: number;
  votes_in_gw?: number;
  success_rate_gw?: number;
  // X/Twitter profile link
  twitter_username?: string | null;
  show_twitter_username?: boolean;
  // Country / scope fields
  country?: string | null;
  rank_movement?: number;
  previous_rank?: number | null;
  pts_to_leader?: number;
  // Captain scope fields
  captain_rating?: number;
  prev_captain_rating?: number;
  captain_hit_rate?: number;
  captains_picked?: number;
  captain_streak?: number;
  captain_top_pick_count?: number;
  captain_valid_gws?: number;
  captain_success_pct?: number | null;
  pinned_trophy?: PublicPinnedTrophy | null;
}

export function useOptimizedLeaderboard(limit: number = 5) {
  const query = useQuery({
    queryKey: ['profiles', 'leaderboard', { limit }],
    queryFn: async () => {
      const res = await getProfilesLeaderboard({ order: 'predictor_rating.desc', limit, offset: 0, scope: 'duels' });
      const entries: LeaderboardEntry[] = res.leaderboard.map((p, index) => mapEntry(p, index + 1));
      return entries;
    },
    staleTime: 60 * 1000,
  });

  return { leaderboard: query.data ?? [], loading: query.isLoading };
}

type LeaderboardQueryResult = {
  entries: LeaderboardEntry[];
  hasMore: boolean;
  total: number;
  myPosition?: LeaderboardEntry;
  scope?: LeaderboardScope;
  scopeCards?: LeaderboardScopeCards;
};

function mapEntry(
  p: Awaited<ReturnType<typeof getProfilesLeaderboard>>['leaderboard'][number],
  fallbackRank: number,
  scope?: LeaderboardScope,
): LeaderboardEntry {
  const isCaptainScope = scope === 'captain';
  return {
    rank: p.rank ?? fallbackRank,
    username: p.username,
    avatar_url: p.avatar_url || undefined,
    predictor_rating:
      isCaptainScope && p.captain_rating != null ? p.captain_rating : (p.predictor_rating ?? 0),
    success_rate: isCaptainScope && p.captain_hit_rate != null ? p.captain_hit_rate * 100 : (p.success_rate ?? 0),
    total_predictions: isCaptainScope && p.captains_picked != null ? p.captains_picked : (p.total_predictions ?? 0),
    user_id: p.user_id,
    rating_change: p.rating_change,
    votes_in_gw: p.votes_in_gw,
    success_rate_gw: p.success_rate_gw,
    twitter_username: p.twitter_username,
    show_twitter_username: p.show_twitter_username,
    country: p.country,
    rank_movement: p.rank_movement,
    previous_rank: p.previous_rank,
    pts_to_leader: p.pts_to_leader,
    captain_rating: p.captain_rating,
    prev_captain_rating: p.prev_captain_rating,
    captain_hit_rate: p.captain_hit_rate,
    captains_picked: p.captains_picked,
    captain_streak: p.captain_streak,
    captain_top_pick_count: p.captain_top_pick_count,
    captain_valid_gws: p.captain_valid_gws,
    captain_success_pct: p.captain_success_pct,
    pinned_trophy: p.pinned_trophy ?? null,
  };
}

function mapLeaderboardResponse(
  res: Awaited<ReturnType<typeof getProfilesLeaderboard>>,
  offset: number,
  limit: number,
  requestedScope: LeaderboardScope,
): LeaderboardQueryResult {
  const resolvedScope = res.scope ?? requestedScope;
  const entries: LeaderboardEntry[] = res.leaderboard.map((p, index) =>
    mapEntry(p, offset + index + 1, resolvedScope),
  );
  const total = res.pagination.total ?? (offset + entries.length + (entries.length === limit ? limit : 0));
  const myPosition = res.my_position
    ? mapEntry(res.my_position, res.my_position.rank, resolvedScope)
    : undefined;
  return {
    entries,
    hasMore: entries.length === limit,
    total,
    myPosition,
    scope: resolvedScope,
    scopeCards: res.scope_cards,
  };
}

export function useLeaderboardWithPagination(
  page: number = 1,
  limit: number = 25,
  searchQuery: string = '',
  gameweekId?: string,
  scope: LeaderboardScope = 'duels',
  apiScopeOverride?: 'country',
  seasonId?: string,
  timeframe: LeaderboardTimeframe = 'alltime',
) {
  const offset = Math.max(0, (page - 1) * limit);
  const effectiveApiScope = apiScopeOverride ?? scope;
  const effectiveGameweekId = timeframe === 'season' ? gameweekId : undefined;
  const effectiveSeasonId =
    timeframe === 'season' && !effectiveGameweekId ? seasonId : undefined;
  const hasTimeFilter = Boolean(effectiveGameweekId || effectiveSeasonId);

  // Season mode without seasonId/gameweekId yet → wait (avoid flashing lifetime all-time).
  const seasonParamsReady =
    timeframe !== 'season' || Boolean(effectiveGameweekId || effectiveSeasonId);

  const query = useQuery<LeaderboardQueryResult>({
    queryKey: [
      'profiles',
      'leaderboard',
      { page, limit, searchQuery, gameweekId: effectiveGameweekId, seasonId: effectiveSeasonId, timeframe, scope, apiScopeOverride },
    ],
    queryFn: async () => {
      const res = await getProfilesLeaderboard({
        order: 'predictor_rating.desc',
        limit,
        offset,
        ...(effectiveGameweekId
          ? { gameweek_id: effectiveGameweekId }
          : effectiveSeasonId
            ? { season_id: effectiveSeasonId }
            : {}),
        search: searchQuery.trim().length > 0 ? searchQuery.trim() : undefined,
        scope: effectiveApiScope,
      });
      return mapLeaderboardResponse(res, offset, limit, scope);
    },
    enabled: seasonParamsReady,
    // Keep prior page only while season/GW params are ready; avoid flashing all-time under season labels.
    placeholderData: seasonParamsReady ? (prev) => prev : undefined,
    staleTime: hasTimeFilter ? 0 : 30 * 1000,
    refetchOnWindowFocus: false,
  });

  const data = query.data;
  const managers = data?.entries ?? [];
  const hasMore = !!data?.hasMore;
  const totalCount = data?.total;
  const totalPages = totalCount ? Math.ceil(totalCount / limit) : undefined;
  const myPosition = data?.myPosition;
  const scopeCards = data?.scopeCards;
  const resolvedScope = data?.scope ?? scope;

  return {
    managers,
    totalCount,
    totalPages,
    loading: !seasonParamsReady || query.isLoading,
    isFetching: query.isFetching,
    error: query.error ?? null,
    hasMore,
    myPosition,
    scopeCards,
    resolvedScope,
  };
}

/**
 * Fetches current user's leaderboard position.
 * For GW mode: prefer `my_position` from leaderboard response (single request).
 * This hook is used only for all-time mode, or when `my_position` is not available.
 */
export function useCurrentUserLeaderboardEntry(userId?: string, gameweekId?: string) {
  const query = useQuery({
    queryKey: ['profiles', 'currentUserEntry', userId, gameweekId],
    queryFn: async () => {
      if (!userId) return undefined;
      if (gameweekId) {
        const res = await getProfilesLeaderboard({
          user_ids: userId,
          gameweek_id: gameweekId,
          limit: 1,
          offset: 0,
          order: 'predictor_rating.desc',
          scope: 'duels',
        });
        const p = res.leaderboard[0];
        if (!p) return undefined;
        return mapEntry(p, p.rank ?? 0);
      }
      const res = await getProfiles({ user_ids: userId, limit: 1, offset: 0 });
      const p = res.profiles[0];
      if (!p) return undefined;
      return {
        rank: p.rank ?? 0,
        username: p.username,
        avatar_url: p.avatar_url || undefined,
        predictor_rating: p.predictor_rating,
        success_rate: p.success_rate,
        total_predictions: p.total_predictions,
        user_id: p.user_id,
        twitter_username: p.twitter_username,
        show_twitter_username: p.show_twitter_username,
        country: p.country,
      } as LeaderboardEntry;
    },
    enabled: !!userId,
    staleTime: gameweekId ? 0 : 30 * 1000,
  });
  return { entry: query.data };
}
